import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCircle, Crown, Globe, Phone, MapPin, Briefcase, Star, PencilSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Link } from 'react-router-dom';
import AppLayout from '@/components/AppLayout';
import BackButton from '@/components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    business_name: '',
    website_url: '',
    phone_number: '',
    business_address: '',
  });
  
  // State pour les avis
  const [myReview, setMyReview] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewData, setReviewData] = useState({
    rating: 5,
    title: '',
    content: '',
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchMyReview();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
      setFormData({
        name: response.data.name || '',
        business_name: response.data.business_name || '',
        website_url: response.data.website_url || '',
        phone_number: response.data.phone_number || '',
        business_address: response.data.business_address || '',
      });
    } catch (error) {
      toast.error(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReview = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews/my-review`, {
        withCredentials: true,
      });
      if (response.data.review) {
        setMyReview(response.data.review);
        setReviewData({
          rating: response.data.review.rating,
          title: response.data.review.title,
          content: response.data.review.content,
        });
      }
    } catch (error) {
      console.error('Error fetching review:', error);
    }
  };

  const submitReview = async () => {
    if (!reviewData.title.trim() || !reviewData.content.trim()) {
      toast.error(t('errors.pleaseFillAllFields'));
      return;
    }
    if (reviewData.content.length < 20) {
      toast.error(t('errors.reviewMinChars'));
      return;
    }

    setSubmittingReview(true);
    try {
      if (myReview) {
        await axios.put(`${BACKEND_URL}/api/reviews`, reviewData, { withCredentials: true });
        toast.success(t('profile.reviewUpdated'));
      } else {
        await axios.post(`${BACKEND_URL}/api/reviews`, reviewData, { withCredentials: true });
        toast.success(t('profile.thankForReview'));
      }
      setShowReviewForm(false);
      fetchMyReview();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.submissionError'));
    } finally {
      setSubmittingReview(false);
    }
  };

  const deleteReview = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/reviews`, { withCredentials: true });
      setMyReview(null);
      setReviewData({ rating: 5, title: '', content: '' });
      toast.success(t('profile.reviewDeleted'));
    } catch (error) {
      toast.error(t('errors.deletionError'));
    }
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/auth/profile`,
        formData,
        { withCredentials: true }
      );
      setUser(response.data);
      setEditing(false);
      toast.success(t('profile.profileUpdated'));
    } catch (error) {
      toast.error(t('errors.savingError'));
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-8 max-w-4xl mx-auto" data-testid="profile-page">
        {/* Back Button */}
        <BackButton to="/dashboard" label="Retour au tableau de bord" className="mb-4" />
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2">{t('profile.title')}</h1>
          <p className="text-muted-foreground">{t('profile.subtitle')}</p>
        </motion.div>

        <div className="space-y-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-xl p-8"
          >
            <div className="flex items-center gap-6 mb-6">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-20 h-20 rounded-full border-2 border-primary"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserCircle size={48} weight="duotone" className="text-primary" />
                </div>
              )}
              <div>
                <h2 className="text-2xl font-bold">{user?.name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">{t('profile.subscriptionStatus')}</h3>
                  <div className="flex items-center gap-2">
                    {user?.subscription_status === 'premium' ? (
                      <>
                        <Crown size={20} weight="fill" className="text-primary" />
                        <span className="text-primary font-medium">{t('profile.premium')}</span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">{t('profile.free')}</span>
                    )}
                  </div>
                </div>
                {user?.subscription_status !== 'premium' && (
                  <Link to="/pricing">
                    <Button data-testid="upgrade-to-premium-btn">{t('profile.upgradeToPremium')}</Button>
                  </Link>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-xl p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-semibold">{t('profile.businessInfo')}</h3>
              {!editing && (
                <Button onClick={() => setEditing(true)} variant="outline">
                  {t('common.modify')}
                </Button>
              )}
            </div>
            
            {editing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('profile.yourName')}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jean Dupont"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_name">{t('profile.companyName')}</Label>
                  <div className="flex items-center gap-2">
                    <Briefcase size={20} className="text-muted-foreground" />
                    <Input
                      id="business_name"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      placeholder="Restaurant La Bella Vita"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website_url">{t('profile.website')} 🌟</Label>
                  <div className="flex items-center gap-2">
                    <Globe size={20} className="text-muted-foreground" />
                    <Input
                      id="website_url"
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      placeholder="https://mon-site.com"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('profile.websiteRedirectDesc')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone_number">{t('profile.phone')} 📞</Label>
                  <div className="flex items-center gap-2">
                    <Phone size={20} className="text-muted-foreground" />
                    <Input
                      id="phone_number"
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('profile.phoneDesc')}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_address">{t('profile.address')} 📍</Label>
                  <div className="flex items-center gap-2">
                    <MapPin size={20} className="text-muted-foreground" />
                    <Input
                      id="business_address"
                      value={formData.business_address}
                      onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                      placeholder="123 Rue de la Paix, 75001 Paris"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{t('profile.addressDesc')}</p>
                </div>

                <div className="flex items-center gap-3 pt-4">
                  <Button onClick={handleSave} data-testid="save-profile-btn">
                    {t('common.save')}
                  </Button>
                  <Button onClick={() => setEditing(false)} variant="ghost">
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {formData.business_name && (
                  <div className="flex items-center gap-3">
                    <Briefcase size={20} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('profile.company')}</p>
                      <p className="font-medium">{formData.business_name}</p>
                    </div>
                  </div>
                )}
                {formData.website_url && (
                  <div className="flex items-center gap-3">
                    <Globe size={20} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('profile.website')}</p>
                      <a href={formData.website_url} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                        {formData.website_url}
                      </a>
                    </div>
                  </div>
                )}
                {formData.phone_number && (
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('profile.phone')}</p>
                      <p className="font-medium">{formData.phone_number}</p>
                    </div>
                  </div>
                )}
                {formData.business_address && (
                  <div className="flex items-center gap-3">
                    <MapPin size={20} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('profile.address')}</p>
                      <p className="font-medium">{formData.business_address}</p>
                    </div>
                  </div>
                )}
                {!formData.website_url && !formData.phone_number && !formData.business_address && !formData.business_name && (
                  <p className="text-muted-foreground text-sm">
                    {t('profile.addBusinessInfo')}
                  </p>
                )}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-xl p-6 bg-primary/5 border border-primary/20"
          >
            <h4 className="font-semibold mb-2">💡 {t('profile.whyImportant')}</h4>
            <ul className="text-sm text-muted-foreground space-y-2">
              <li>✅ {t('profile.importantReasons.redirect')}</li>
              <li>✅ {t('profile.importantReasons.call')}</li>
              <li>✅ {t('profile.importantReasons.localSeo')}</li>
              <li>✅ {t('profile.importantReasons.conversions')}</li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="glass rounded-xl p-8"
          >
            <h3 className="font-semibold mb-4">{t('profile.accountInfo')}</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('profile.memberSince')}</p>
                <p className="font-medium">
                  {new Date(user?.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Section Avis */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="glass rounded-xl p-8"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{t('profile.yourReview')}</h3>
              {myReview && !showReviewForm && (
                <Button variant="outline" size="sm" onClick={() => setShowReviewForm(true)}>
                  <PencilSimple size={16} className="mr-1" />
                  {t('common.modify')}
                </Button>
              )}
            </div>

            {showReviewForm || (!myReview && !showReviewForm) ? (
              <div className="space-y-4">
                <div>
                  <Label>{t('profile.rating')}</Label>
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setReviewData({ ...reviewData, rating: star })}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Star
                          size={32}
                          weight={star <= reviewData.rating ? 'fill' : 'regular'}
                          className={star <= reviewData.rating ? 'text-yellow-400' : 'text-white/20'}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('profile.reviewTitle')}</Label>
                  <Input
                    value={reviewData.title}
                    onChange={(e) => setReviewData({ ...reviewData, title: e.target.value })}
                    placeholder={t('profile.reviewTitlePlaceholder')}
                    maxLength={100}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('profile.reviewContent')}</Label>
                  <Textarea
                    value={reviewData.content}
                    onChange={(e) => setReviewData({ ...reviewData, content: e.target.value })}
                    placeholder={t('profile.reviewContentPlaceholder')}
                    rows={4}
                    maxLength={1000}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {reviewData.content.length}/1000
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button onClick={submitReview} disabled={submittingReview}>
                    {submittingReview ? t('common.sending') : myReview ? t('profile.updateReview') : t('profile.publishReview')}
                  </Button>
                  {showReviewForm && myReview && (
                    <>
                      <Button variant="ghost" onClick={() => setShowReviewForm(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button variant="ghost" className="text-red-400" onClick={deleteReview}>
                        {t('common.delete')}
                      </Button>
                    </>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  {t('profile.reviewDisclaimer')}
                </p>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        size={16}
                        weight={star <= myReview.rating ? 'fill' : 'regular'}
                        className={star <= myReview.rating ? 'text-yellow-400' : 'text-white/20'}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">
                    {t('profile.reviewPublished')}
                  </span>
                </div>
                <h4 className="font-semibold mb-1">{myReview.title}</h4>
                <p className="text-sm text-muted-foreground">{myReview.content}</p>
              </div>
            )}}
          </motion.div>
        </div>
      </div>
    </AppLayout>
  );
}
